import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { portalTournamentAPI } from '../../../services/api';
import { shouldShowGolfGeniusLink } from '../../../utils/tournamentPhases';

/**
 * PortalTeeSheetTab - Shows the player's assigned group prominently,
 * with the full tee sheet below. Links out to GolfGenius on gameday.
 */
const PortalTeeSheetTab = () => {
  const { tournamentId } = useParams();
  const { tournament, portalStatus } = useOutletContext();
  const [teeSheet, setTeeSheet] = useState(null);
  const [myGroup, setMyGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullSheet, setShowFullSheet] = useState(false);

  useEffect(() => {
    const fetchTeeSheet = async () => {
      try {
        const res = await portalTournamentAPI.getTeeSheet(tournamentId);
        const data = res.data?.data || {};
        setTeeSheet(data.teeSheet || null);
        setMyGroup(data.myGroup || null);
      } catch (err) {
        console.error('Failed to load tee sheet:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeeSheet();
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const showGGLink = shouldShowGolfGeniusLink(portalStatus) && tournament?.golfGeniusUrl;

  return (
    <div className="space-y-4">
      {/* GolfGenius link on gameday */}
      {showGGLink && (
        <a
          href={tournament.golfGeniusUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-orange-500 text-white rounded-xl p-4 text-center font-semibold shadow-md hover:bg-orange-600 transition-colors"
        >
          View Live Scoring on GolfGenius
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )}

      {/* My Group - prominent display */}
      {myGroup ? (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-green-800">Your Group</h3>
            {myGroup.teeTime && (
              <span className="text-lg font-bold text-green-700">{myGroup.teeTime}</span>
            )}
          </div>
          <div className="space-y-2">
            {(myGroup.players || []).map((p, i) => (
              <div
                key={i}
                className={`flex items-center justify-between p-2 rounded-lg ${
                  p.isMe ? 'bg-green-200/50 font-medium' : 'bg-white/50'
                }`}
              >
                <span className="text-sm text-gray-800">
                  {p.isMe && <span className="text-green-600 mr-1">&#9679;</span>}
                  {p.name}
                </span>
                <span className="text-xs text-gray-500">
                  HCP {p.handicap != null ? p.handicap : '--'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-4 text-center text-gray-500 text-sm">
          Your tee time has not been assigned yet.
        </div>
      )}

      {/* Full tee sheet toggle */}
      {teeSheet && teeSheet.groups && teeSheet.groups.length > 0 && (
        <div>
          <button
            onClick={() => setShowFullSheet(!showFullSheet)}
            className="w-full py-2 text-sm text-gray-600 font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {showFullSheet ? 'Hide Full Tee Sheet' : 'View Full Tee Sheet'}
          </button>

          {showFullSheet && (
            <div className="mt-3 space-y-2">
              {teeSheet.groups.map((group, gi) => (
                <div
                  key={gi}
                  className={`border rounded-lg p-3 ${
                    myGroup && group.teeTime === myGroup.teeTime
                      ? 'border-green-300 bg-green-50/50'
                      : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500">Group {gi + 1}</span>
                    <span className="text-sm font-semibold text-gray-700">{group.teeTime || '--'}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {(group.players || []).map((p) => p.name || 'Open').join(' | ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!teeSheet && !myGroup && (
        <p className="text-xs text-gray-400 text-center">
          The tee sheet will be available once published by the tournament director.
        </p>
      )}
    </div>
  );
};

export default PortalTeeSheetTab;
